import { isUnbreakablePackageType, type SrPartialPackingTypeMap } from '@plato/data/starrocks/types';
import { DialogStateManager, useDialog } from '@plato/ui-legacy/components/dialog/dialog-provider';
import { Button } from '@plato/ui-legacy/components/ui/button';
import { InputNumber } from '@plato/ui-legacy/components/ui/input-number';
import {
    Dialog,
    DialogBody,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogIconClose,
    DialogTitle,
} from '@plato/ui/containers/dialog';
import type { FocusEventHandler, KeyboardEventHandler, ReactElement } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IncrementalQuantityField } from '../../../components/forms/incremental-quantity-field.tsx';

type IncrementalQuantityDialogProps = {
    onValueChange: (value: number) => void;
    currency: string;
    quantity: number;
    quantityUnit: string;
    salesUnitsPerPackageUnit?: number;
    partialPackingType?: SrPartialPackingTypeMap;
};

const IncrementalQuantityDialog = DialogStateManager.create(
    ({
        onValueChange,
        currency,
        salesUnitsPerPackageUnit,
        quantity,
        quantityUnit,
        partialPackingType,
    }: IncrementalQuantityDialogProps) => {
        const { visible, remove } = useDialog();
        const { t } = useTranslation('sales-draft');

        const [newQuantity, setNewQuantity] = useState(quantity);

        const isCurrentValueAcceptable = salesUnitsPerPackageUnit
            ? newQuantity % salesUnitsPerPackageUnit === 0
            : true;

        return (
            <Dialog open={visible} onClose={remove} disablePointerDismissal>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <div className="flex min-w-0 flex-1 flex-col gap-tight">
                            <DialogTitle>{t('sales_draft__incremental_quantity_dialog__title')}</DialogTitle>
                            <div className="text-body text-content-weak">
                                {t('sales_draft__incremental_quantity_dialog__description')}:
                                {` ${salesUnitsPerPackageUnit || ''} ${quantityUnit}`}
                            </div>
                        </div>
                        <DialogIconClose label={t('form__close', { ns: 'common' })} />
                    </DialogHeader>
                    <DialogBody>
                        <IncrementalQuantityField
                            currency={currency}
                            onValueChange={(value: number): void => {
                                setNewQuantity(value);
                            }}
                            value={newQuantity}
                            salesUnitsPerPackageUnit={salesUnitsPerPackageUnit}
                            quantityUnit={quantityUnit}
                            partialPackingType={partialPackingType}
                        />
                    </DialogBody>
                    <DialogFooter>
                        <Button
                            variant="primary"
                            onClick={() => {
                                onValueChange(newQuantity);
                                remove();
                            }}
                            disabled={!isCurrentValueAcceptable}
                        >
                            {t('sales_draft__incremental_quantity_dialog__confirm')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        );
    },
);

export type IncrementalQuantityFieldWithDialogProps = {
    autoFocus?: boolean;
    currency: string;
    name?: string;
    onValueChange: (value: number) => void;
    value: number;
    quantityUnit: string;
    salesUnitsPerPackageUnit?: number;
    partialPackingType?: SrPartialPackingTypeMap;
    onBlur?: FocusEventHandler<HTMLInputElement> | undefined;
    onFocus?: FocusEventHandler<HTMLInputElement> | undefined;
    onKeyDown?: KeyboardEventHandler<HTMLInputElement> | undefined;
    isLoading?: boolean;
};

export function IncrementalQuantityFieldWithDialog({
    onValueChange,
    currency,
    value: quantity,
    isLoading,
    salesUnitsPerPackageUnit,
    quantityUnit,
    partialPackingType = 'UNKNOWN',
    ...rest
}: IncrementalQuantityFieldWithDialogProps): ReactElement {
    const handleClick = (): void => {
        const hasToBeMultipleOfX = isUnbreakablePackageType(partialPackingType);

        if (hasToBeMultipleOfX) {
            void DialogStateManager.show(IncrementalQuantityDialog, {
                onValueChange,
                currency,
                quantity,
                quantityUnit,
                salesUnitsPerPackageUnit,
                partialPackingType,
            });
        }
    };

    return (
        <InputNumber
            currency={currency}
            {...rest}
            value={quantity}
            onValueChange={({ value }) => {
                onValueChange(Number(value));
            }}
            suffix={` ${quantityUnit}`}
            isAllowed={(values) => {
                const { floatValue } = values;
                return floatValue !== undefined && floatValue > 0;
            }}
            loading={isLoading}
            onClick={handleClick}
        />
    );
}
